import { useState, useCallback, useMemo } from "react";
import { Leaf, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const BRANDS = [
  { name: "A La Goma", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//a-la-goma.png" },
  { name: "Alcochem", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//alcochem.png" },
  { name: "Allister", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//allister.png" },
  { name: "Birdbarier", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//birdbarier.png" },
  { name: "Cabel", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//cabel.png" },
  { name: "Compo", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//compo.png" },
  { name: "Dynatrap", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//dynatrap.png" },
  { name: "Ecotrap", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//ecotrap.png" },
  { name: "Elanco", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//elanco.png" },
  { name: "GES TLC", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//ges-tlc.png" },
  { name: "Grease Monkey", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//grease-monkey.png" },
  { name: "JT Eaton", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//jt-eaton.png" },
  { name: "Lancelab", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//lancelab.png" },
  { name: "Liphatech", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//liphatech.png" },
  { name: "London Foggers", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//londonfoggers.png" },
  { name: "Ovocontrol", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//ovocontrol.png" },
  { name: "Polti", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//polti.png" },
  { name: "Quimix", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//quimix.png" },
  { name: "Sami Tech", logoUrl: "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/brand-logos//sami-tech.png" }
];

const BrandPartners = () => {
  const [isPaused, setIsPaused] = useState(false);
  const [offset, setOffset] = useState(0);
  
  // Memoize duplicated brands to prevent recalculation
  const duplicatedBrands = useMemo(() => [...BRANDS, ...BRANDS], []);
  const itemWidth = 160;

  const handlePrev = useCallback(() => {
    setOffset(prev => prev + itemWidth);
    setTimeout(() => {
      if (offset > itemWidth * BRANDS.length) {
        setOffset(0);
      }
    }, 300);
  }, [offset]);

  const handleNext = useCallback(() => {
    setOffset(prev => prev - itemWidth);
    setTimeout(() => {
      if (Math.abs(offset) > itemWidth * BRANDS.length) {
        setOffset(0);
      }
    }, 300);
  }, [offset]);

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-28 bg-background relative overflow-hidden">
      {/* Subtle nature pattern */}
      <div className="absolute inset-0 bg-nature-pattern opacity-30" aria-hidden="true"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-8 sm:mb-10 md:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 bg-accent/80 border border-primary/15 text-primary rounded-full px-4 py-2 sm:px-5 sm:py-2.5 md:px-6 md:py-3 mb-4 sm:mb-6 md:mb-8 shadow-nature">
            <Leaf className="w-4 h-4 md:w-5 md:h-5" />
            <span className="text-xs md:text-sm font-semibold">Nuestros Partners</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-foreground mb-3 sm:mb-4 md:mb-6 px-2">
            Principales Marcas
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed px-4">
            Trabajamos con las marcas líderes en la industria del control de plagas
          </p>
        </div>

        {/* Carrusel con flechas */}
        <div 
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Flechas de navegación */}
          <Button
            variant="outline"
            size="icon"
            onClick={handlePrev}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-background/90 backdrop-blur-sm border-border shadow-lg hover:bg-primary hover:text-primary-foreground transition-colors"
            aria-label="Anterior"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            onClick={handleNext}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-background/90 backdrop-blur-sm border-border shadow-lg hover:bg-primary hover:text-primary-foreground transition-colors"
            aria-label="Siguiente"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>

          {/* Gradientes de fade en los bordes */}
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 lg:w-32 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" aria-hidden="true"></div>
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 lg:w-32 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" aria-hidden="true"></div>
          
          {/* Contenedor del carrusel - using CSS transform for better performance */}
          <div 
            className="flex will-change-transform"
            style={{ 
              animation: isPaused ? 'none' : 'carousel 30s linear infinite',
              transform: isPaused ? `translateX(${offset}px)` : undefined,
              transition: isPaused ? 'transform 0.3s ease-out' : undefined
            }}
          >
            {duplicatedBrands.map((brand, index) => (
              <div
                key={`${brand.name}-${index}`}
                className="flex-shrink-0 flex items-center justify-center px-4 sm:px-6 md:px-8 py-3 md:py-4"
              >
                <img 
                  src={brand.logoUrl} 
                  alt={`Logo de ${brand.name}`}
                  className="w-24 sm:w-28 md:w-32 h-14 sm:h-16 md:h-20 object-contain grayscale invert brightness-50 opacity-70 hover:opacity-100 transition-opacity duration-300"
                  loading="lazy"
                  decoding="async"
                  width="128"
                  height="80"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandPartners;
